/**
 * Connect your own software: API keys (to read your sites, bookings and
 * orders, and update a booking) and webhooks (we call you when something
 * changes, signed with X-Lulu-Signature).
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { Card, ErrorNote, Field, Spinner, SubHeader } from '../components/Primitives';
import { CopyLink } from './Team';

interface Keys {
  keys: {
    id: string;
    name: string;
    last4: string;
    createdAt: string;
    lastUsedAt: string | null;
  }[];
  webhooks: { id: string; url: string; lastStatus: number | null; lastAt: string | null }[];
}

export function ConnectPage() {
  const T = useT();
  const [data, setData] = useState<Keys | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState<{ label: string; value: string } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = () =>
    api
      .get<Keys>('/v1/partner/api-keys')
      .then(setData, (e: unknown) => setError(e instanceof ApiError ? e.message : 'Error'));
  useEffect(() => {
    void load();
  }, []);
  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const run = async (fn: () => Promise<void>) => {
    setMsg(null);
    try {
      await fn();
      void load();
    } catch (err) {
      setMsg(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    }
  };

  return (
    <div className="space-y-4">
      <SubHeader icon="plug" title={T('अपना सॉफ़्टवेयर जोड़ें', 'Connect your software')} />
      <p className="text-sm text-muted">
        {T(
          'अगर आपका अपना सिस्टम है, तो वो लुलु से आपकी साइट, बुकिंग और ऑर्डर पढ़ सकता है।',
          'If you run your own system, it can read your sites, bookings and orders from Lulu.',
        )}
      </p>
      {secret && <CopyLink link={secret.value} note={secret.label} />}
      {msg && <p className="text-sm text-offline">{msg}</p>}

      <Card>
        <p className="mb-2 font-semibold">{T('API चाबियाँ', 'API keys')}</p>
        <ul className="divide-y divide-line">
          {data.keys.map((k) => (
            <li key={k.id} className="flex items-center justify-between gap-2 py-2 text-sm">
              <span>
                {k.name} <span className="font-mono text-xs text-muted">…{k.last4}</span>
                <span className="block text-xs text-muted">
                  {k.lastUsedAt
                    ? `${T('आख़िरी बार', 'Last used')} ${new Date(k.lastUsedAt).toLocaleDateString()}`
                    : T('अभी इस्तेमाल नहीं हुई', 'Not used yet')}
                </span>
              </span>
              <button
                className="btn-quiet"
                onClick={() => void run(() => api.post(`/v1/partner/api-keys/${k.id}/revoke`))}
              >
                {T('बंद करें', 'Revoke')}
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-2 flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={T('नाम, जैसे “हमारा CRM”', 'Name, e.g. “Our CRM”')}
          />
          <button
            className="btn-primary !w-auto px-4"
            disabled={!name.trim()}
            onClick={() =>
              void run(async () => {
                const r = await api.post<{ key: string }>('/v1/partner/api-keys', { name });
                setSecret({
                  label: T(
                    'अभी कॉपी कर लें — दोबारा नहीं दिखेगी।',
                    'Copy it now — it won’t be shown again.',
                  ),
                  value: r.key,
                });
                setName('');
              })
            }
          >
            {T('बनाएँ', 'Create')}
          </button>
        </div>
      </Card>

      <Card>
        <p className="mb-2 font-semibold">Webhooks</p>
        <ul className="divide-y divide-line">
          {data.webhooks.map((h) => (
            <li key={h.id} className="flex items-center justify-between gap-2 py-2 text-sm">
              <span className="min-w-0">
                <span className="block truncate font-mono text-xs">{h.url}</span>
                <span className="text-xs text-muted">
                  {h.lastStatus === null
                    ? T('अभी कोई कॉल नहीं', 'No calls yet')
                    : h.lastStatus >= 200 && h.lastStatus < 300
                      ? `✓ ${h.lastStatus}`
                      : `✗ ${h.lastStatus || T('नहीं पहुँचा', 'unreachable')}`}
                </span>
              </span>
              <button
                className="btn-quiet"
                onClick={() => void run(() => api.post(`/v1/partner/webhooks/${h.id}/remove`))}
              >
                {T('हटाएँ', 'Remove')}
              </button>
            </li>
          ))}
        </ul>
        <Field label={T('आपका https पता', 'Your https address')}>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://"
            inputMode="url"
          />
        </Field>
        <button
          className="btn-secondary mt-2"
          disabled={!url.startsWith('https://')}
          onClick={() =>
            void run(async () => {
              const r = await api.post<{ secret: string }>('/v1/partner/webhooks', { url });
              setSecret({
                label: T(
                  'यह सीक्रेट संभालकर रखें। हर कॉल पर X-Lulu-Signature: sha256=HMAC(secret, body) आता है।',
                  'Keep this secret safe. Each call carries X-Lulu-Signature: sha256=HMAC(secret, body).',
                ),
                value: r.secret,
              });
              setUrl('');
            })
          }
        >
          {T('जोड़ें', 'Add')}
        </button>
      </Card>
    </div>
  );
}
